import React from "react"
import Icon from "@kit/design/icon"
import Stack from "@kit/design/stack"
import pluralize from "pluralize"
import { Menu, MenuButton, MenuList } from "@reach/menu-button"
import { DiscoveryHub } from "app/discovery-hub"
import { Notifications } from "../../../../engines/notifications/javascript"
import { Avatar } from "core/avatar"
import { settings } from "./utils/menu-definitions"
import { SettingsItem } from "./utils/settings-item"
import { useAccountOptions } from "./utils/use-account-options"
import { TrialNav } from "./trial-nav"
import { UpgradeCallout } from "./upgrade-callout/index"
import { FreePlanUpgradeButton } from "./free-plan-upgrade-button"
import { useVerticalNavOptIn } from "./utils/use-vertical-nav-optin"
import { analytics } from "utils/analytics"

import "./settings-nav.css"

export function SettingsNav({ userInfo }) {
  const settingOptions = useAccountOptions(userInfo)
  const { optInEnabled, enableVerticalNav } = useVerticalNavOptIn()
  const daysLeft = pluralize("day", userInfo.trial_days_remaining, true)

  const SettingsButton = ({ isExpanded }) => {
    const iconTransform = isExpanded && "transform -rotate-180"
    const activeButtonStyles = isExpanded
      ? "active text-black"
      : "hover:text-black hover:bg-warm-white-100 focus:text-black"

    function handleClick() {
      if (!isExpanded) {
        analytics.track(
          "Element",
          "Clicked",
          {
            element_type: "button",
            name: "nav_section_settings",
            text: "Settings",
            location: "settings_nav"
          },
          { transport: "beacon" }
        )
      }
    }

    return (
      <MenuButton
        className={`inline-flex items-center focus:outline-none gap-2 p-2 rounded nav-hover relative focus:bg-blue-600 ${activeButtonStyles} text-black focus:bg-warm-white-200`}
        style={{ padding: "4px 4px 4px 12px" }}
        title={userInfo.name}
        onClick={handleClick}
      >
        <Avatar email={userInfo.email} />
        <span
          className="align-middle font-semibold truncate"
          style={{ maxWidth: 200 }}
        >
          {userInfo.name}
        </span>
        <Icon
          name="down"
          className={`duration-200 ${iconTransform}`}
          aria-hidden
        />
      </MenuButton>
    )
  }

  return (
    <div className="hidden lg:flex items-center gap-2 relative">
      <DiscoveryHub />
      <Notifications path={"/notifications"} />
      <TrialNav userInfo={userInfo} daysLeft={daysLeft} />
      <FreePlanUpgradeButton userInfo={userInfo} />
      <Menu>
        {({ isExpanded }) => (
          <>
            <SettingsButton isExpanded={isExpanded} />
            <MenuList className="rounded shadow-lg settings-menu bg-white mt-2 relative z-10">
              <UpgradeCallout userInfo={userInfo} />
              <Stack spacing={2} className="mb-2">
                {Object.entries(
                  settings(userInfo.current_admin, settingOptions, {
                    enabled: optInEnabled,
                    onSelect: enableVerticalNav
                  })
                ).map(([menu, options]) => (
                  <SettingsItem key={menu} {...options} />
                ))}
              </Stack>
            </MenuList>
          </>
        )}
      </Menu>
    </div>
  )
}
